import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Chip,
  TablePagination,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  Divider,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaUsers,
  FaImages,
  FaUserShield,
  FaFilter,
  FaFileExport,
  FaChartBar,
  FaColumns,
  FaFileDownload,
  FaCog,
  FaPrint,
  FaTable,
  FaChartPie,
  FaChartLine,
  FaSave,
  FaUndo,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";
import { CSVLink } from "react-csv";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement } from "chart.js";
import { Pie, Bar, Line } from "react-chartjs-2";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";

// Register ChartJS components
ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement);


const FamilyFlexiReportsChild = () => {
  let navigate = useNavigate();
  const [{ user }] = useAtom(gbState);
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [visibleColumns, setVisibleColumns] = useState({
    family_code: true,
    family_name: true,
    primary_member: true,
    mobile: true,
    email: true,
    city: true,
    state: true,
    country: true,
    gender: true,
    blood_group: false,
    qualification: false,
    birth_place: false,
  });
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [viewMenuAnchorEl, setViewMenuAnchorEl] = useState(null);
  const [reportType, setReportType] = useState("table");
  const [chartType, setChartType] = useState("pie");
  const [groupByField, setGroupByField] = useState("country");
  const [savedReports, setSavedReports] = useState([]);
  const [reportName, setReportName] = useState("");
  const [saveReportModalOpen, setSaveReportModalOpen] = useState(false); // Ensure this is false by default

      useEffect(() => {
  const getDataFromServer = () => {
    let userToken = localStorage.getItem("token");

    let urlLink = `${process.env.REACT_APP_API_URL}/family/${user.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        const oldData = response.data.families;
        console.log("Raw data sample:", oldData[0]);
        const updatedData = oldData.map((data) => {
          return {
            ...data,
            id: data?._id,
            family_code: data?.family_code,
            family_name: data?.family_name,
            primary_member:
              data?.primary_member_id?.first_name +
              " " +
              data?.primary_member_id?.last_name,
            mobile: data?.primary_member_id?.mobile,
            email: data?.primary_member_id?.email,
            city: data?.city?.label || "",
            state: data?.state?.label || "",
            status: data?.status,
            gender: data?.gender,
            country: data?.country?.label || "",
            blood_group: data?.blood_group || "",
            qualification: data?.qualification || "",
            birth_place: data?.birth_place || "",
          };
        });

        setData(updatedData);
        setFilteredData(updatedData);
        
        // Load saved reports from localStorage
        const savedReportsData = localStorage.getItem('familyFlexiReports');
        if (savedReportsData) {
          setSavedReports(JSON.parse(savedReportsData));
        }
      })
      .catch(function (err) {
        console.log("error api call ", err);
        toast.error("Error fetching data");
      });
  };
  getDataFromServer();
}, [user.church_id]);

// Filter data based on search term
useEffect(() => {
  if (searchTerm.trim() === "") {
    setFilteredData(data);
  } else {
    const filtered = data.filter((item) => {
      return (
        (item.family_name && item.family_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.primary_member && item.primary_member.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.family_code && item.family_code.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.email && item.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.city && item.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.state && item.state.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.country && item.country.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    });
    setFilteredData(filtered);
  }
}, [searchTerm, data]);

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle column visibility menu
  const handleColumnMenuClick = (event) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleColumnToggle = (columnName) => {
    setVisibleColumns({
      ...visibleColumns,
      [columnName]: !visibleColumns[columnName],
    });
  };

  // Handle export menu
  const handleExportMenuClick = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchorEl(null);
  };

  // Handle view menu
  const handleViewMenuClick = (event) => {
    setViewMenuAnchorEl(event.currentTarget);
  };

  const handleViewMenuClose = () => {
    setViewMenuAnchorEl(null);
  };

  // Handle report type change
  const handleReportTypeChange = (type) => {
    setReportType(type);
    handleViewMenuClose();
  };

  // Handle chart type change
  const handleChartTypeChange = (type) => {
    setChartType(type);
    handleViewMenuClose();
  };

  // Handle group by field change
  const handleGroupByChange = (event) => {
    setGroupByField(event.target.value);
  };

  // Prepare CSV data for export
  const csvData = useMemo(() => {
    const headers = Object.keys(visibleColumns)
      .filter(key => visibleColumns[key])
      .map(key => ({
        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
        key: key
      }));

    return {
      headers,
      data: filteredData
    };
  }, [filteredData, visibleColumns]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const groupedData = {};
    
    filteredData.forEach(item => {
      const groupValue = item[groupByField] || 'Unknown';
      if (!groupedData[groupValue]) {
        groupedData[groupValue] = 0;
      }
      groupedData[groupValue]++;
    });

    const labels = Object.keys(groupedData);
    const values = Object.values(groupedData);
    const backgroundColors = labels.map((_, index) => {
      const hue = (index * 137.5) % 360; // Golden angle approximation for good distribution
      return `hsl(${hue}, 70%, 60%)`;
    });

    return {
      labels,
      datasets: [
        {
          label: `Families by ${groupByField.charAt(0).toUpperCase() + groupByField.slice(1).replace(/_/g, ' ')}`,
          data: values,
          backgroundColor: backgroundColors,
          borderColor: backgroundColors.map(color => color.replace('60%', '50%')),
          borderWidth: 1,
        },
      ],
    };
  }, [filteredData, groupByField]);

  // Save current report configuration
  const handleSaveReport = () => {
    if (!reportName.trim()) {
      toast.error("Please enter a report name");
      return;
    }

    const newReport = {
      id: Date.now(),
      name: reportName,
      config: {
        visibleColumns,
        reportType,
        chartType,
        groupByField
      }
    };

    const updatedReports = [...savedReports, newReport];
    setSavedReports(updatedReports);
    localStorage.setItem('familyFlexiReports', JSON.stringify(updatedReports));
    
    setReportName('');
    setSaveReportModalOpen(false);
    toast.success("Report saved successfully");
  };

  // Load a saved report
  const handleLoadReport = (report) => {
    setVisibleColumns(report.config.visibleColumns);
    setReportType(report.config.reportType);
    setChartType(report.config.chartType);
    setGroupByField(report.config.groupByField);
    toast.success(`Report "${report.name}" loaded successfully`);
  };

  // Delete a saved report
  const handleDeleteReport = (reportId) => {
    confirmAlert({
      title: "Confirm deletion",
      message: "Are you sure you want to delete this saved report?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            const updatedReports = savedReports.filter(report => report.id !== reportId);
            setSavedReports(updatedReports);
            localStorage.setItem('familyFlexiReports', JSON.stringify(updatedReports));
            toast.success("Report deleted successfully");
          }
        },
        {
          label: "No",
          onClick: () => {}
        }
      ]
    });
  };

  // Print current report
  const handlePrint = () => {
    // Create a print-friendly version
    const printWindow = window.open('', '_blank');
    
    // Generate title based on current view and filters
    const title = `Family Report - ${reportType === 'chart' ? 
      `${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart by ${groupByField}` : 
      'Table View'}`;
    
    // Start building HTML content
    let printContent = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #333; text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .summary-stats { display: flex; justify-content: space-around; margin-bottom: 30px; }
            .stat-card { border: 1px solid #ddd; border-radius: 5px; padding: 15px; width: 30%; }
            .stat-title { font-weight: bold; margin-bottom: 5px; }
            .stat-value { font-size: 24px; color: #2563eb; }
            .print-date { text-align: right; color: #666; margin-top: 30px; font-size: 12px; }
            @media print { body { -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <div class="print-date">Generated on: ${new Date().toLocaleString()}</div>
    `;
    
    // Add summary stats
    printContent += `
      <div class="summary-stats">
        <div class="stat-card">
          <div class="stat-title">Total Families</div>
          <div class="stat-value">${filteredData.length}</div>
        </div>
    `;
    
    if (reportType === 'chart') {
      // Add chart-specific stats
      const labels = chartData.labels;
      const values = chartData.datasets[0].data;
      const maxIndex = values.indexOf(Math.max(...values));
      
      printContent += `
        <div class="stat-card">
          <div class="stat-title">Categories</div>
          <div class="stat-value">${labels.length}</div>
        </div>
        <div class="stat-card">
          <div class="stat-title">Most Common</div>
          <div class="stat-value">${labels[maxIndex] || 'N/A'}</div>
        </div>
      </div>
      `;
    } else {
      printContent += `</div>`; // Close summary-stats if not chart
    }
    
    // Add table data for table view
    if (reportType === 'table') {
      printContent += `
        <table>
          <thead>
            <tr>
      `;
      
      // Add table headers
      Object.keys(visibleColumns).forEach(column => {
        if (visibleColumns[column]) {
          printContent += `<th>${column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, ' ')}</th>`;
        }
      });
      
      printContent += `
            </tr>
          </thead>
          <tbody>
      `;
      
      // Add table rows
      filteredData.forEach(row => {
        printContent += `<tr>`;
        Object.keys(visibleColumns).forEach(column => {
          if (visibleColumns[column]) {
            const cellValue = typeof row[column] === 'object' && row[column] !== null ? 
              (row[column].label || JSON.stringify(row[column])) : 
              row[column];
            printContent += `<td>${cellValue || ''}</td>`;
          }
        });
        printContent += `</tr>`;
      });
      
      printContent += `
          </tbody>
        </table>
      `;
    }
    
    // Close HTML
    printContent += `
        </body>
      </html>
    `;
    
    // Write to the new window and print
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load before printing
    printWindow.onload = function() {
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
    
    handleExportMenuClose();
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen w-full">
      <div className="flex flex-col space-y-4">
        {/* Header with title and actions */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Family Flexible Reports</h1>
          
          <div className="flex space-x-2">
            {/* Save Report Button */}
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<FaSave />}
              onClick={() => setSaveReportModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Save Report
            </Button>
            
            {/* View Options Menu */}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FaTable />}
              onClick={handleViewMenuClick}
              className="border-blue-500 text-blue-500"
            >
              View Options
            </Button>
            <Menu
              anchorEl={viewMenuAnchorEl}
              open={Boolean(viewMenuAnchorEl)}
              onClose={handleViewMenuClose}
            >
              <MenuItem onClick={() => handleReportTypeChange("table")}>
                <FaTable className="mr-2" /> Table View
              </MenuItem>
              <MenuItem onClick={() => handleReportTypeChange("chart")}>
                <FaChartBar className="mr-2" /> Chart View
              </MenuItem>
              {reportType === "chart" && (
                <>
                  <Divider />
                  <MenuItem onClick={() => handleChartTypeChange("pie")}>
                    <FaChartPie className="mr-2" /> Pie Chart
                  </MenuItem>
                  <MenuItem onClick={() => handleChartTypeChange("bar")}>
                    <FaChartBar className="mr-2" /> Bar Chart
                  </MenuItem>
                  <MenuItem onClick={() => handleChartTypeChange("line")}>
                    <FaChartLine className="mr-2" /> Line Chart
                  </MenuItem>
                </>
              )}
            </Menu>
            
            {/* Column Visibility Menu */}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FaColumns />}
              onClick={handleColumnMenuClick}
              className="border-blue-500 text-blue-500"
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnMenuAnchorEl}
              open={Boolean(columnMenuAnchorEl)}
              onClose={handleColumnMenuClose}
            >
              {Object.keys(visibleColumns).map((column) => (
                <MenuItem key={column} onClick={() => handleColumnToggle(column)}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visibleColumns[column]}
                        onChange={() => handleColumnToggle(column)}
                        color="primary"
                      />
                    }
                    label={column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, ' ')}
                  />
                </MenuItem>
              ))}
            </Menu>
            
            {/* Export Menu */}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FaFileExport />}
              onClick={handleExportMenuClick}
              className="border-blue-500 text-blue-500"
            >
              Export
            </Button>
            <Menu
              anchorEl={exportMenuAnchorEl}
              open={Boolean(exportMenuAnchorEl)}
              onClose={handleExportMenuClose}
            >
              <CSVLink 
                data={filteredData} 
                headers={csvData.headers}
                filename={`family-report-${new Date().toISOString().slice(0,10)}.csv`}
                className="no-underline text-gray-800"
              >
                <MenuItem onClick={handleExportMenuClose}>
                  <FaFileDownload className="mr-2" /> Export as CSV
                </MenuItem>
              </CSVLink>
              <MenuItem onClick={handlePrint}>
                <FaPrint className="mr-2" /> Print Report
              </MenuItem>
            </Menu>
          </div>
        </div>
        
        {/* Search and Filter Bar */}
        <div className="flex flex-wrap gap-4 items-center bg-white p-4 rounded-lg shadow-sm mb-4">
          <div className="flex-grow">
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <FaSearch className="text-gray-400 mr-2" />,
              }}
              placeholder="Search by name, email, location..."
            />
          </div>
          
          {reportType === "chart" && (
            <div className="min-w-[200px]">
              <FormControl fullWidth size="small">
                <InputLabel>Group By</InputLabel>
                <Select
                  value={groupByField}
                  label="Group By"
                  onChange={handleGroupByChange}
                >
                  <MenuItem value="country">Country</MenuItem>
                  <MenuItem value="state">State</MenuItem>
                  <MenuItem value="city">City</MenuItem>
                  <MenuItem value="gender">Gender</MenuItem>
                  <MenuItem value="blood_group">Blood Group</MenuItem>
                </Select>
              </FormControl>
            </div>
          )}
        </div>
        
        {/* Saved Reports Section */}
        {savedReports.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
            <Typography variant="h6" className="mb-2 font-semibold">Saved Reports</Typography>
            <div className="flex flex-wrap gap-2">
              {savedReports.map((report) => (
                <Chip
                  key={report.id}
                  label={report.name}
                  onClick={() => handleLoadReport(report)}
                  onDelete={() => handleDeleteReport(report.id)}
                  color="primary"
                  variant="outlined"
                  className="m-1"
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Report Content */}
        <div className="bg-white p-4 rounded-lg shadow-sm w-full">
          {reportType === "table" ? (
            /* Table View */
            <>
              <TableContainer component={Paper} className="mt-4 w-full">
                <Table size="small" className="w-full">
                  <TableHead>
                    <TableRow className="bg-gray-100">
                      {Object.keys(visibleColumns).map(
                        (column) =>
                          visibleColumns[column] && (
                            <TableCell key={column} className="font-semibold">
                              {column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, ' ')}
                            </TableCell>
                          )
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => (
                        <TableRow key={row.id} hover>
                          {Object.keys(visibleColumns).map(
                            (column) =>
                              visibleColumns[column] && (
                                <TableCell key={column}>{typeof row[column] === 'object' && row[column] !== null ? 
                  (row[column].label || JSON.stringify(row[column])) : 
                  row[column]}</TableCell>
                              )
                          )}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          ) : (
            /* Chart View */
            <div className="p-4 w-full">
              <div className="flex justify-center w-full">
                <div className="w-full max-h-[60vh] flex flex-col items-center">
                  <Typography variant="h6" className="mb-4 text-center font-semibold">
                    {`Family Distribution by ${groupByField.charAt(0).toUpperCase() + groupByField.slice(1).replace(/_/g, ' ')}`}
                  </Typography>
                  <div className="h-[400px] w-full max-w-[800px]">
                    {chartType === "pie" && <Pie data={chartData} options={{ 
                      responsive: true, 
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: 'right',
                          labels: {
                            boxWidth: 15,
                            padding: 10
                          }
                        }
                      }
                    }} />}
                    {chartType === "bar" && <Bar data={chartData} options={{ 
                      responsive: true, 
                      maintainAspectRatio: true,
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }} />}
                    {chartType === "line" && <Line data={chartData} options={{ 
                      responsive: true, 
                      maintainAspectRatio: true,
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }} />}
                  </div>
                </div>
              </div>
              
              {/* Summary Stats */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-blue-50">
                  <CardContent>
                    <Typography variant="h6" className="font-semibold">Total Families</Typography>
                    <Typography variant="h4">{filteredData.length}</Typography>
                  </CardContent>
                </Card>
                
                <Card className="bg-green-50">
                  <CardContent>
                    <Typography variant="h6" className="font-semibold">Categories</Typography>
                    <Typography variant="h4">{chartData.labels.length}</Typography>
                  </CardContent>
                </Card>
                
                <Card className="bg-purple-50">
                  <CardContent>
                    <Typography variant="h6" className="font-semibold">Most Common</Typography>
                    <Typography variant="h4">
                      {chartData.labels.length > 0 ? 
                        chartData.labels[chartData.datasets[0].data.indexOf(Math.max(...chartData.datasets[0].data))] : 
                        'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Save Report Modal */}
      {saveReportModalOpen && (
        <Modal
          isOpen={saveReportModalOpen}
          onClose={() => setSaveReportModalOpen(false)}
          title="Save Report Configuration"
        >
        <div className="p-4">
          <TextField
            label="Report Name"
            variant="outlined"
            fullWidth
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            className="mb-4"
          />
          <div className="flex justify-end space-x-2">
            <Button 
              variant="outlined" 
              onClick={() => setSaveReportModalOpen(false)}
              startIcon={<FaUndo />}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSaveReport}
              startIcon={<FaSave />}
            >
              Save Report
            </Button>
          </div>
        </div>
        </Modal>
      )}
    </div>
  )
}

export default FamilyFlexiReportsChild